import { reflect } from '@effector/reflect';
import { Button, TextInput } from '@mantine/core';
import { combine } from 'effector';
import { useUnit } from 'effector-react';
import { equals, not } from 'patronum';
import { devicePageInfoModel, devicePageModel } from 'pages/device-page/model';
import { createDeleteDeviceDetailedModal } from 'features/delete-device';
import { DEVICE_NAME_MAX_LENGTH, DEVICE_NAME_MIN_LENGTH } from 'entities/device';
import { authModel, USER_ROLES } from 'shared/api';
import { $t, translated } from 'shared/lib';
import cls from './device-page-form.module.scss';

const DeleteDeviceModal = createDeleteDeviceDetailedModal(devicePageInfoModel.deleteDeviceModel);

const NameField = reflect({
	view: TextInput,
	bind: {
		value: devicePageInfoModel.form.fields.name.$value,
		onChange: (e) => devicePageInfoModel.form.fields.name.change(e.target.value),
		label: translated('device-page.name'),
		placeholder: translated('device-page.name'),
		errorProps: { 'data-test-id': 'device-name-input-error' },
		error: combine(
			[devicePageInfoModel.form.fields.name.$errors, $t],
			([errors, t]) =>
				errors.length > 0 &&
				t(`errors:validation.${errors[0]}`, {
					fieldName: t('device-page.name'),
					maxLength: DEVICE_NAME_MAX_LENGTH,
					minLength: DEVICE_NAME_MIN_LENGTH,
				}),
		),
		readOnly: not(equals(authModel.$role, USER_ROLES.god)),
		required: true,
	},
});

const AccountField = reflect({
	view: TextInput,
	bind: {
		value: devicePageInfoModel.form.fields.account.$value,
		label: translated('device-page.account'),
		readOnly: true,
	},
});

const CashdeskField = reflect({
	view: TextInput,
	bind: {
		value: devicePageInfoModel.form.fields.cashdesk.$value,
		label: translated('device-page.cashdesk'),
		readOnly: true,
	},
});

const GameField = reflect({
	view: TextInput,
	bind: {
		value: devicePageInfoModel.form.fields.game.$value,
		label: translated('device-page.game'),
		readOnly: true,
	},
});

const CurrencyField = reflect({
	view: TextInput,
	bind: {
		value: devicePageInfoModel.form.fields.currency.$value,
		label: translated('device-page.currency'),
		readOnly: true,
	},
});

const PinField = reflect({
	view: TextInput,
	bind: {
		value: devicePageInfoModel.form.fields.pin.$value,
		label: translated('device-page.pin'),
		readOnly: true,
	},
});

const PlayersField = reflect({
	view: TextInput,
	bind: {
		value: devicePageInfoModel.form.fields.players.$value,
		label: translated('device-page.players'),
		readOnly: true,
	},
});

const StatusField = reflect({
	view: TextInput,
	bind: {
		value: combine(
			{
				t: $t,
				value: devicePageInfoModel.form.fields.status.$value,
			},
			({ t, value }) => (value ? t('device-page.active') : t('device-page.not-active')),
		),
		label: translated('device-page.status'),
		readOnly: true,
	},
});

const SaveButton = reflect({
	view: Button,
	bind: {
		type: 'submit',
		disabled: devicePageInfoModel.form.$pristine,
		children: translated('device-page.save'),
		loading: devicePageInfoModel.editDeviceMutation.$pending,
		className: cls.devicePageFormFooterOption,
	},
});

export const DevicePageForm = () => {
	const [device, submit] = useUnit([devicePageModel.$device, devicePageInfoModel.form.submit]);

	return (
		device !== null && (
			<>
				<form
					className={cls.devicePageForm}
					onSubmit={(event) => {
						event.preventDefault();
						submit();
					}}
				>
					<NameField />
					<AccountField />
					<CashdeskField />
					<GameField />
					<CurrencyField />
					<PinField />
					<PlayersField />
					<StatusField />
					<div className={cls.devicePageFormFooter}>
						<SaveButton />
						<DeleteDeviceModal.Button className={cls.devicePageFormFooterOption} />
					</div>
				</form>
				<DeleteDeviceModal />
			</>
		)
	);
};
